import React, { useState, useCallback, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import styled from 'styled-components'

import AutoComplete from 'components/AutoComplete'
import { Button } from '@nexiahome/ui-common/components/primitives/buttons'
import Spinner from 'components/Spinner'

import DealerInfo from 'pages/shared/DealerInfo'
import DealerAggregates from 'pages/MigrateDealer/DealerAggregates'

import useDealers from 'hooks/useDealers'
import useActivePremiumAccount from 'hooks/useActivePremiumAccount'
import useActiveHehmAccount from 'hooks/useActiveHehmAccount'
import { useServerErrorContext } from 'hooks/ServerErrorContext'

import Api from 'services/api'

import { ButtonContainer, PageHeader, PageTitle, LoadingSinglePanePage, SinglePanePage, screenSize } from 'styles/shared'
import { color } from 'styles/colors'

import { useConfirmDialog } from 'hooks/useDialog'

import { CONFIRMATION_DIALOG_MESSAGES } from 'helpers/confirmationDialogMessages'

const MigrateDealer = () => {
  const navigate = useNavigate()
  const { id: fromSelectedDealerId } = useParams()

  const { dealers, areDealersLoaded } = useDealers({ page: 1, pageSize: 'max' })

  const { setServerError } = useServerErrorContext()

  const getName = (dealer) => {
    return dealer && (dealer.name || `Unknown (${dealer.id})`)
  }

  const options = useMemo(() => {
    return dealers.map(dealer => {
      return { ...dealer, name: `${getName(dealer)} - ${dealer.phoneNumber}` }
    })
  }, [dealers])

  const [showSpinner, setShowSpinner] = useState(false)
  const [showDone, setShowDone] = useState(false)
  const [toDealerInfo, setToDealerInfo] = useState(null)

  const fromDealerInfo = useMemo(() => dealers.find(({ id }) => id === parseInt(fromSelectedDealerId)) || {}, [dealers, fromSelectedDealerId])

  const sameDealerError = toDealerInfo?.id != null && toDealerInfo.id === fromDealerInfo?.id

  const { activeAccount: fromDealerIsPremium, isActiveAccountLoaded: fromDealerIsPaywallAcctLoaded } = useActivePremiumAccount(fromDealerInfo?.id)
  const { activeAccount: toDealerIsPremium, isActiveAccountLoaded: toDealerIsPaywallAcctLoaded } = useActivePremiumAccount(toDealerInfo?.id)
  const { activeHehmAccount: fromDealerHehmAccount, isActiveHehmAccountLoaded: fromDealerIsHehmAcctLoaded } = useActiveHehmAccount(fromDealerInfo?.id)
  const { activeHehmAccount: toDealerHehmAccount, isActiveHehmAccountLoaded: toDealerIsHehmAcctLoaded } = useActiveHehmAccount(toDealerInfo?.id)

  const { confirm } = useConfirmDialog()

  const onClickTransfer = useCallback(async () => {
    if (fromDealerHehmAccount && !toDealerHehmAccount && fromDealerIsHehmAcctLoaded && toDealerIsHehmAcctLoaded) {
      await confirm({
        title: 'Migration Not Allowed',
        description: 'The destination dealer must have an active EHMKT account before migrating from a dealer with an active EHMKT account.',
        confirmText: 'OK'
      })
      return
    }

    let confirmationDialogMessage = `Do you want to transfer from ${getName(fromDealerInfo)} dealer to ${getName(toDealerInfo)}?`
    if (fromDealerIsPremium && !toDealerIsPremium && fromDealerIsPaywallAcctLoaded && toDealerIsPaywallAcctLoaded) {
      confirmationDialogMessage += '\nWarning: There will be some functionality loss to customers migrating to standard Diagnostics.'
    }
    if (fromDealerInfo?.hehmDevicesCount > 0) {
      confirmationDialogMessage += '\nWarning: EHMKT devices will be deprovisioned and will need to be re-activated after the migration is complete.'
    }

    const isConfirmed = await confirm({
      title: 'Confirm Transfer',
      description: confirmationDialogMessage,
      confirmText: CONFIRMATION_DIALOG_MESSAGES.BUTTON_TEXT.CONFIRM
    })

    if (isConfirmed) {
      setShowSpinner(true)

      Api.Dealers.migrate(fromDealerInfo.id, toDealerInfo.id)
        .then(() => {
          setShowSpinner(false)
          setShowDone(true)
        })
        .catch(() => {
          setShowSpinner(false)
          setServerError('Failed to migrate')
        })
    }
  }, [fromDealerInfo, toDealerInfo, setServerError, fromDealerIsPremium, toDealerIsPremium, fromDealerIsPaywallAcctLoaded, toDealerIsPaywallAcctLoaded, fromDealerHehmAccount, toDealerHehmAccount, fromDealerIsHehmAcctLoaded, toDealerIsHehmAcctLoaded, confirm])

  if (!areDealersLoaded) {
    return <LoadingSinglePanePage />
  }

  return (
    <>
      <PageHeader>
        <PageTitle>Migration between Dealer</PageTitle>
      </PageHeader>
      <S.SinglePanePage>
        <S.Container>
          <S.InnerContainer>
            <AutoComplete
              label="From this dealer"
              options={options}
              onSelected={(dealer) => { navigate(`/admin/dealers/${dealer.id}/migrate`) }}
              initialValue={getName(fromDealerInfo) + ' - ' + (fromDealerInfo && fromDealerInfo.phoneNumber)}
              getOptionKey={(option) => option.id}
              getOptionLabel={(option) => option.name}
            />
            <S.Dealer>
              <DealerInfo dealer={fromDealerInfo} showDone={showDone} />
              <DealerAggregates id={fromDealerInfo.id} />
            </S.Dealer>
          </S.InnerContainer>

          {showSpinner
            ? <S.Spinner variant="secondaryButton" />
            : <S.Icon data-testid="right-arrow" className="icon-arrow-right4" />
          }
          <S.InnerContainer>
            <AutoComplete
              options={options}
              label="To this dealer"
              initialValue={toDealerInfo?.name || ''}
              onSelected={(dealer) => { setToDealerInfo(dealer) }}
              getOptionKey={(option) => option.id}
              getOptionLabel={(option) => option.name}
            />
            <S.Dealer>
              {sameDealerError && (
                <S.SameDealerAlert role="alert"> Migration between the same dealer is not allowed.</S.SameDealerAlert>
              )}
              {toDealerInfo && !sameDealerError &&
              <>
                <DealerInfo dealer={toDealerInfo} showDone={showDone} />
                <DealerAggregates id={toDealerInfo.id} />
              </>}
            </S.Dealer>
          </S.InnerContainer>
        </S.Container>

        {!showDone
          ? (
            <ButtonContainer>
              <Button decoration="secondary" styles={{ button: { marginRight: 15 } }} text="Cancel" onPress={() => navigate('/admin/dealers')} />
              <Button decoration="positive" enabled={!!toDealerInfo && !sameDealerError} text="Transfer" onPress={onClickTransfer} showSpinner={showSpinner} />
            </ButtonContainer>
          )
          : (
            <ButtonContainer>
              <S.Info>Moved customer successfully</S.Info>
              <Button decoration="positive" text="Back to dealer" onPress={() => navigate('/admin/dealers')} />
            </ButtonContainer>
          )}
      </S.SinglePanePage>
    </>
  )
}

export default MigrateDealer

const S = {
  Heading: styled.h4`
    font-weight: 600;
  `,
  Info: styled.span`
    display: block;
    padding-bottom: 10px;
    color: ${color('headerText')};
  `,
  Container: styled.div`
    display: flex;
    justify-content: space-around;
  `,
  InnerContainer: styled.div`
    display: flex;
    width: 45%;
    flex-direction: column;
  `,
  Dealer: styled.div`
    min-height: 350px;
    padding: 10px;
    margin-top: 10px;
    border: 1px solid ${color('border')};
  `,
  Spinner: styled(Spinner)`
    margin: 60px 0 0 0;
    width: 32px;
    height: 32px;
    border-radius: 18px;
  `,
  Icon: styled.i`
    font-size: 32px;
    font-weight: 100;
    margin-top: 60px;
  `,
  SameDealerAlert: styled.p`
    margin: 0 0 10px;
    padding: 10px 14px;
    font-size: 14px;
    font-weight: 600;
    color: ${color('danger')};
    background: ${color('semiDestructiveButtonHover')};
    border: 1px solid ${color('danger')};
    border-radius: 4px;
  `,
  SinglePanePage: styled(SinglePanePage)`
    @media ${screenSize.small} {
      margin-top: 0;
    }
  `
}
